import {
  addStepToWorkflow,
  BranchInfo,
  findBranchContainingStep,
  getNewTriggerConfigFromSelectedOption,
} from "#src/batteries-included-components/Drawers/Workflows/WorkflowConfigurationDrawer/WorkflowConfigurationAddNodeDrawer.helpers";
import {
  WorkflowConfigStepType,
  WorkflowParallelTasksType,
  WorkflowStepType,
  WorkflowTriggerDisplayType,
  WorkflowTriggerEventType,
  WorkflowTriggerType,
} from "@validereinc/domain";
import { Workflows } from "@validereinc/domain-controllers";
import {
  Edge,
  EdgeMouseHandler,
  Node,
  NodeMouseHandler,
  useReactFlow,
} from "@xyflow/react";
import { cloneDeep } from "es-toolkit/compat";
import { produce } from "immer";
import { nanoid } from "nanoid";
import { WorkflowGraphEdgeType } from "../WorkflowGraphEdge/types";
import { WorkflowGraphNodeType } from "../WorkflowGraphNode/types";
import { useWorkflowGraphState } from "./WorkflowGraphStore";
import { deleteStepFromWorkflow } from "./helpers/deleteWorkflowGraphStep.helpers";
import { remapIdsForParallelBranches } from "./helpers/cloneWorkflowGraphStep.helpers";
import {
  getWorkflowConfigurationTriggerIndexFromTriggerKey,
  getWorkflowConfigurationTriggerKeyFromIndex,
} from "./useWorkflowGraph.helpers";

const { flattenWorkflowSteps, getAllWorkflowSteps } = Workflows.logic;

export const useWorkflowGraphInteractions: () => {
  zoomIn: () => Promise<boolean>;
  zoomOut: () => Promise<boolean>;
  fitView: () => Promise<boolean>;
  onEdgeMouseEnter: EdgeMouseHandler;
  onEdgeMouseLeave: EdgeMouseHandler;
  onNodeMouseEnter: NodeMouseHandler;
  onNodeMouseLeave: NodeMouseHandler;
  onNodeSelect: NodeMouseHandler;
  onNodeDeselect: () => void;
  onEnterEditMode: () => void;
  onExitEditMode: () => void;
  removeDummyTriggers: () => void;
  onAddStep: (edge: Edge) => void;
  onAddTrigger: (triggerToAdd: WorkflowTriggerEventType & "scheduled") => void;
  onCopyStep: (step?: WorkflowConfigStepType) => void;
  onPasteStep: (edge: Edge) => void;
  onDeleteStep: (stepIdToDelete?: string) => boolean;
  onDeleteTrigger: (triggerIdToDelete?: string) => boolean;
} = () => {
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  const {
    nodes,
    edges,
    setNodes,
    setEdges,
    onNodeHoverEnter,
    onNodeHoverLeave,
    selectNode,
    currentConfiguration,
    setCurrentConfiguration,
    isInEditMode,
    isSelectingNodeToSourceFormDataFrom,
    deselectNode,
    setIsInEditMode,
    currentTriggers,
    setCurrentTriggers,
    setIsDummyNodeSelected,
    setNextStepIdForNewStep,
    setPreviousStepIdForNewStep,
    markNodeAsDirty,
    remapTriggerLayoutAfterDeletion,
    stepToCopy,
    setStepToCopy,
    setStepIdToDelete,
  } = useWorkflowGraphState();

  const onEdgeMouseEnter: EdgeMouseHandler = (_, hoveredEdge: Edge) => {
    if (isSelectingNodeToSourceFormDataFrom) {
      return;
    }

    setNodes(
      nodes.map((newNode) => {
        const isConnected = [hoveredEdge.source, hoveredEdge.target].includes(
          newNode.id
        );

        return {
          ...newNode,
          data: {
            ...newNode.data,
            isTranslucent: !isConnected,
            isHovered: isConnected,
          },
        } as WorkflowGraphNodeType;
      })
    );

    setEdges(
      edges.map((newEdge) => {
        return {
          ...newEdge,
          data: {
            ...newEdge.data,
            isDisabled: newEdge.id !== hoveredEdge.id,
            isHovered: newEdge.id === hoveredEdge.id,
          },
        };
      })
    );
  };

  const onEdgeMouseLeave: EdgeMouseHandler = () => {
    if (isSelectingNodeToSourceFormDataFrom) {
      return;
    }

    setNodes(
      nodes.map((newNode) => {
        const newData = { ...newNode.data };
        delete newData.isTranslucent;
        delete newData.isHovered;
        return {
          ...newNode,
          data: newData,
        } as WorkflowGraphNodeType;
      })
    );

    setEdges(
      edges.map((newEdge) => {
        const newData: WorkflowGraphEdgeType["data"] = { ...newEdge.data };
        delete newData.isDisabled;
        delete newData.isHovered;

        return {
          ...newEdge,
          data: newData,
        } as WorkflowGraphEdgeType;
      })
    );
  };

  const onNodeMouseEnter: NodeMouseHandler = (_, node: Node) => {
    onNodeHoverEnter(node.id);
  };

  const onNodeMouseLeave: NodeMouseHandler = (_, node: Node) => {
    onNodeHoverLeave(node.id);
  };

  const onNodeSelect: NodeMouseHandler = (_, node: Node) => {
    if (node.data.variant === "parallel") {
      return;
    }

    if (isSelectingNodeToSourceFormDataFrom) {
      const flattenedSteps = currentConfiguration
        ? flattenWorkflowSteps(currentConfiguration.steps)
        : {};
      const hasOutput = flattenedSteps[node.id]?.task?.output !== undefined;
      if (!hasOutput) {
        return;
      }
    }

    if (node.data.variant === "trigger" && node.data.isDummy) {
      setIsDummyNodeSelected(true);
    } else {
      setIsDummyNodeSelected(false);
    }

    selectNode(node.id);

    const connectedEdges = edges.filter(
      (edge) => edge.source === node.id || edge.target === node.id
    );

    const connectedEdgeIds = connectedEdges.map((edge) => edge.id);

    setEdges(
      edges.map((newEdge) => {
        const amIConnected = connectedEdgeIds.includes(newEdge.id);
        return {
          ...newEdge,
          data: {
            ...newEdge.data,
            isDisabled: !amIConnected,
            isSelected: amIConnected,
          },
        };
      })
    );

    const nextLevelNodeIds = Array.from(
      new Set(connectedEdges.map((edge) => [edge.target, edge.source]).flat())
    );

    setNodes(
      nodes.map((newNode) => {
        const shouldSelect = nextLevelNodeIds.includes(newNode.id);

        return {
          ...newNode,
          data: {
            ...newNode.data,
            isTranslucent: !shouldSelect && newNode.id !== node.id,
            isSelected: shouldSelect && newNode.id === node.id,
          },
        } as WorkflowGraphNodeType;
      })
    );

    // TODO: This needs a revision:
    // Bring the selected node into view, without changing zoom:
    // const currentZoom = getZoom();
    // fitView({
    //   nodes: [{ id: node.id }],
    //   duration: 500,
    //   minZoom: currentZoom,
    //   maxZoom: currentZoom,
    // });
  };

  const onNodeDeselect = () => {
    deselectNode();
  };

  const onEnterEditMode = () => {
    if (isInEditMode) return;

    setIsInEditMode(true);

    const currentTriggersHasDummyTrigger = currentTriggers?.some(
      (trigger) => trigger.type === WorkflowTriggerDisplayType.DUMMY
    );

    if (!currentTriggersHasDummyTrigger) {
      setCurrentTriggers([
        ...(currentTriggers ?? ([] as WorkflowTriggerType[])),
        {
          type: WorkflowTriggerDisplayType.DUMMY,
          event: WorkflowTriggerDisplayType.DUMMY,
        },
      ]);
    }
  };

  const resetNewStepState = () => {
    setStepToCopy(undefined);
    setPreviousStepIdForNewStep(undefined);
    setNextStepIdForNewStep(undefined);
    setStepIdToDelete(undefined);
  };

  const onExitEditMode = () => {
    setIsInEditMode(false);
    removeDummyTriggers();
    resetNewStepState();
  };

  const removeDummyTriggers = () => {
    setCurrentTriggers(
      currentTriggers?.filter(
        (trigger) => trigger.type !== WorkflowTriggerDisplayType.DUMMY
      ) ?? []
    );
  };

  ///////////////////////////////
  // Add step logic
  ///////////////////////////////

  const onAddStep = (edge: Edge) => {
    deselectNode();
    setIsDummyNodeSelected(true);
    setNextStepIdForNewStep(edge.target);
    setPreviousStepIdForNewStep(edge.source);
  };

  const onAddTrigger = (
    triggerToAdd: WorkflowTriggerEventType & "scheduled"
  ) => {
    const newTrigger = getNewTriggerConfigFromSelectedOption(triggerToAdd);
    const currentTriggersHasDummyTrigger = currentTriggers?.some(
      (trigger) => trigger.type === WorkflowTriggerDisplayType.DUMMY
    );
    setCurrentTriggers([
      ...(currentTriggers ?? ([] as WorkflowTriggerType[])),
      newTrigger as WorkflowTriggerType,
      ...(!currentTriggersHasDummyTrigger
        ? [
            {
              type: WorkflowTriggerDisplayType.DUMMY,
              event: WorkflowTriggerDisplayType.DUMMY,
            },
          ]
        : []),
    ]);
    // Omit dummy trigger from count
    const newNodeId = getWorkflowConfigurationTriggerKeyFromIndex(
      currentTriggers?.length ?? 0
    );
    setIsDummyNodeSelected(false);
    selectNode(newNodeId);
    markNodeAsDirty(newNodeId);
  };

  const onCopyStep = (step?: WorkflowConfigStepType) => {
    if (!step) {
      return;
    }

    const clonedStep = cloneDeep(step);

    clonedStep.next = undefined;
    clonedStep.end = false;

    delete clonedStep.visualization_metadata;

    onEnterEditMode();
    setStepToCopy(clonedStep);
  };

  const onPasteStep = (edge: Edge) => {
    deselectNode();

    const previousStepId = edge.source;
    const nextStepId = edge.target;

    if (!currentConfiguration || !stepToCopy) {
      return;
    }

    // Check if nextStepId is a valid step ID (not a visual node like workflow-node-*-end)
    const allStepIds = Object.keys(
      getAllWorkflowSteps(currentConfiguration.steps)
    );
    const isValidNextStepId = Boolean(
      nextStepId && allStepIds.includes(nextStepId)
    );

    const clonedStepToInsert = cloneDeep(stepToCopy);
    if (clonedStepToInsert.type === WorkflowStepType.PARALLEL) {
      remapIdsForParallelBranches(
        clonedStepToInsert,
        nextStepId,
        isValidNextStepId
      );
    }

    const newStepId = `${clonedStepToInsert?.type === WorkflowStepType.PARALLEL ? "parallel" : clonedStepToInsert?.task?.type}-step-${nanoid(8)}`;

    let branchInfo: BranchInfo = {
      willBeAddedToBranch: false,
      parallelStepId: undefined,
    };

    setCurrentConfiguration(
      produce(currentConfiguration, (draft) => {
        branchInfo = addStepToWorkflow(
          draft,
          clonedStepToInsert as Exclude<
            WorkflowConfigStepType,
            WorkflowParallelTasksType
          >,
          newStepId,
          previousStepId,
          isValidNextStepId ? nextStepId : undefined,
          markNodeAsDirty
        );
      })
    );

    if (stepToCopy?.type === WorkflowStepType.PARALLEL) {
      markNodeAsDirty(newStepId);
    } else if (branchInfo.willBeAddedToBranch && branchInfo.parallelStepId) {
      markNodeAsDirty(newStepId);
      markNodeAsDirty(branchInfo.parallelStepId);
    } else {
      selectNode(newStepId);
      markNodeAsDirty(newStepId);
    }

    resetNewStepState();
  };

  const onDeleteStep = (stepIdToDelete?: string): boolean => {
    if (!stepIdToDelete || !currentConfiguration) return false;

    const allSteps = getAllWorkflowSteps(currentConfiguration.steps);
    const stepToDelete = allSteps[stepIdToDelete];

    if (!stepToDelete) return false;

    // Track whether the step was actually deleted
    let stepWasDeleted = false;

    let parallelStepIdToMarkDirty: string | undefined;

    setCurrentConfiguration(
      produce(currentConfiguration, (draft) => {
        // Check if step exists before deletion
        const allStepsBefore = getAllWorkflowSteps(draft.steps);
        const stepExistsBefore = allStepsBefore[stepIdToDelete] !== undefined;
        // Check if step was in a branch before deletion
        const branchInfo = findBranchContainingStep(draft, stepIdToDelete);

        // Find the parallel step ID before deletion
        // We need to do this before deletion because branchInfo.parallelStep is a Draft reference
        if (branchInfo) {
          for (const [stepId, step] of Object.entries(draft.steps)) {
            if (
              step.type === WorkflowStepType.PARALLEL &&
              step === branchInfo.parallelStep
            ) {
              parallelStepIdToMarkDirty = stepId;
              break;
            }
          }
        }

        deleteStepFromWorkflow(draft, stepIdToDelete);

        // Check if step exists after deletion
        const allStepsAfter = getAllWorkflowSteps(draft.steps);
        const stepExistsAfter = allStepsAfter[stepIdToDelete] !== undefined;

        stepWasDeleted = stepExistsBefore && !stepExistsAfter;
      })
    );

    // Mark the parallel step as dirty after the state update
    if (parallelStepIdToMarkDirty) {
      markNodeAsDirty(parallelStepIdToMarkDirty);
    }

    deselectNode();

    if (!stepWasDeleted) {
      return false;
    }

    markNodeAsDirty(stepIdToDelete);
    setStepIdToDelete(undefined);

    return true;
  };

  const onDeleteTrigger = (triggerIdToDelete?: string): boolean => {
    if (!triggerIdToDelete?.startsWith("trigger-") || !currentTriggers) {
      return false;
    }

    const triggerIndex =
      getWorkflowConfigurationTriggerIndexFromTriggerKey(triggerIdToDelete);
    const triggerToDelete = currentTriggers[triggerIndex];

    if (
      !triggerToDelete ||
      triggerToDelete.type === WorkflowTriggerDisplayType.DUMMY
    ) {
      return false;
    }

    remapTriggerLayoutAfterDeletion(triggerIndex);
    setCurrentTriggers(
      currentTriggers.filter((_, index) => index !== triggerIndex)
    );
    markNodeAsDirty(triggerIdToDelete);
    deselectNode();
    setStepIdToDelete(undefined);

    return true;
  };

  return {
    zoomIn,
    zoomOut,
    fitView,
    onEdgeMouseEnter,
    onEdgeMouseLeave,
    onNodeMouseEnter,
    onNodeMouseLeave,
    onNodeSelect,
    onNodeDeselect,
    onEnterEditMode,
    onExitEditMode,
    removeDummyTriggers,
    onAddStep,
    onAddTrigger,
    onCopyStep,
    onPasteStep,
    onDeleteStep,
    onDeleteTrigger,
  };
};
